import { IPriceDelta } from "./listingPriceDelta";
import { FRESH_LISTING_DAYS } from "./listingAge";

// How many of the same part off the same car count as "stock heavy" - at that point another relist
// at the same price just competes with the yard's own listings.
export const STOCK_HEAVY_THRESHOLD = 5;

export type NextStepKey =
  | "fixListing"
  | "addPartNumbers"
  | "reprice"
  | "addFields"
  | "discountStock"
  | "relist"
  | "none";

export type NextStepTone = "blocked" | "attention" | "go" | "stock" | "neutral";

export interface INextStep {
  key: NextStepKey;
  label: string;
  tone: NextStepTone;
}

export interface INextStepInput {
  /** checkEbayDisabled says the listing cannot be relisted as it stands. */
  relistBlocked: boolean;
  /** The part has an MPN or an OE number. */
  hasPartNumbers: boolean;
  /** Fixable fields the listing score found missing (MPN, OEMPN, KType, Brand, Grade, ...). */
  missingFixable: string[];
  priceDelta: IPriceDelta;
  /** Age of the listing that is live now, in days; null when unknown. */
  listingAgeDays: number | null;
  /** Parts in stock of the same category off the same year/make/model. */
  stockByVehicle: number;
}

const STEPS: Record<NextStepKey, INextStep> = {
  fixListing: { key: "fixListing", label: "Fix listing", tone: "blocked" },
  addPartNumbers: { key: "addPartNumbers", label: "Add part numbers", tone: "attention" },
  reprice: { key: "reprice", label: "Reprice", tone: "attention" },
  addFields: { key: "addFields", label: "Add fields", tone: "attention" },
  discountStock: { key: "discountStock", label: "Discount stock", tone: "stock" },
  relist: { key: "relist", label: "Relist", tone: "go" },
  none: { key: "none", label: "Nothing to do", tone: "neutral" },
};

/**
 * The one thing to do next with a listing, in the order the queue triages: a listing that cannot
 * be relisted is fixed first; one with no part numbers at all gets them before anything else is
 * worth doing; an overpriced listing is repriced before its lesser gaps are filled; stock-heavy
 * parts priced to market are discounted rather than relisted; and a part that is fine but has sat
 * a while is relisted. Anything else is left alone.
 */
export function computeNextStep(input: INextStepInput): INextStep {
  if (input.relistBlocked) return STEPS.fixListing;
  if (!input.hasPartNumbers) return STEPS.addPartNumbers;
  if (input.priceDelta.tone === "above") return STEPS.reprice;
  if (input.missingFixable.length > 0) return STEPS.addFields;

  const withinMarket = input.priceDelta.tone === "within" || input.priceDelta.tone === "noSold";
  if (input.stockByVehicle >= STOCK_HEAVY_THRESHOLD && withinMarket) return STEPS.discountStock;

  if (input.listingAgeDays != null && input.listingAgeDays > FRESH_LISTING_DAYS) return STEPS.relist;

  return STEPS.none;
}
